import { useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { GetAttachmentDownloadUrlDocument, GetAttachmentDownloadUrlQuery } from './attachmentDownloadUrl.generated'

// Signed URLs last 15 minutes, so every file, failed ones included, is looked up again every 10.
const REFRESH_MS = 10 * 60 * 1000

type Resolved = Record<string, { url: string | null; round: number }>

// Signed URLs come from `server`; advanced/service has no GCS access (ADR-087).
export const useAttachmentDownloadUrls = (fileIDs: string[]): Record<string, string | null> => {
  const client = useApolloClient()
  const [round, setRound] = useState(0)
  const [resolved, setResolved] = useState<Resolved>({})
  const requested = useRef(new Set<string>())

  useEffect(() => {
    const interval = setInterval(() => setRound((current) => current + 1), REFRESH_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const stale = fileIDs.filter(
      (fileID) => resolved[fileID]?.round !== round && !requested.current.has(`${fileID}:${round}`)
    )
    if (stale.length === 0) {
      return
    }
    stale.forEach((fileID) => requested.current.add(`${fileID}:${round}`))

    Promise.all(
      stale.map((fileID) =>
        client
          .query<GetAttachmentDownloadUrlQuery>({
            query: GetAttachmentDownloadUrlDocument,
            variables: { fileID },
            fetchPolicy: 'no-cache',
          })
          .then((result) => [fileID, result.data.patientFile.downloadURL ?? null] as const)
          .catch(() => [fileID, null] as const)
      )
    )
      .then((entries) => {
        setResolved((current) => ({
          ...current,
          ...Object.fromEntries(
            entries.map(([fileID, url]) => [fileID, { url: url ?? current[fileID]?.url ?? null, round }])
          ),
        }))
      })
      .catch(() => undefined)
  }, [fileIDs, client, resolved, round])

  return useMemo(
    () => Object.fromEntries(fileIDs.map((fileID) => [fileID, resolved[fileID]?.url ?? null])),
    [fileIDs, resolved]
  )
}
